/*
  best-product.js
  Hydrates the Best Product section: per-card More Details toggle, three card
  accordions (Pros & Cons, Ratings, Our Take), testing-criteria accordion,
  rating bar fills, sleep-expert blockquote injection, sticky-table cells,
  sticky CTA, and the table-of-contents scroll-spy. Each initializer no-ops
  if its DOM is absent.
*/

(function () {
  'use strict';

  /* ── Sticky table first column ─────────────────────────────────────
     On horizontal scroll, the first column gets a shadow via .is-sticky. */
  function initStickyTableCells(root) {
    var tables = root.querySelectorAll('.table-container, .best-product__compare-container');
    tables.forEach(function (tableElem) {
      tableElem.addEventListener('scroll', function (e) {
        var stickyCells = tableElem.querySelectorAll('td:nth-child(1)');
        var scrolled = e.target.scrollLeft > 0;
        stickyCells.forEach(function (cell) {
          cell.classList.toggle('is-sticky', scrolled);
        });
      });
    });
  }

  /* ── Headerless table detection ───────────────────────────────────
     WP body tables ship no <thead>/<th>, so the CSS styles the first row
     as a header by default. That's wrong for key/value tables (e.g. a
     "factor | description" list) whose first row is just data. Tag those
     with .table--no-header so the CSS skips the header treatment.
     Conservative: only opt out when a first-row cell holds a long block of
     text — a real header's cells are short labels — so genuine header rows
     are never demoted. */
  function initTableHeaders(root) {
    var tables = root.querySelectorAll('.scrollable-table table');
    tables.forEach(function (table) {
      // Explicit header markup is authoritative — leave it alone.
      if (table.tHead || table.querySelector('th')) return;
      var firstRow = table.querySelector('tr');
      if (!firstRow) return;
      var cells = firstRow.children;
      if (cells.length < 2) return;
      for (var i = 0; i < cells.length; i++) {
        if ((cells[i].textContent || '').trim().length > 60) {
          table.classList.add('table--no-header');
          return;
        }
      }
    });
  }

  /* ── Per-card More Details toggle ─────────────────────────────────
     Reveals the additional content panel and swaps the toggle label. */
  function initCardDetailsToggle(root) {
    var toggles = root.querySelectorAll('[data-card-details-toggle]');
    toggles.forEach(function (button) {
      var card = button.closest('.best-product__card');
      if (!card) return;
      var additional = card.querySelector('[data-card-additional]');
      if (!additional) return;
      var label = button.querySelector('.best-product__card-details-label');
      // Labels come from the button's data attributes so they stay translatable
      // in Liquid; fall back to English when the attributes are absent.
      var moreLabel = button.dataset.labelMore || 'More Details';
      var hideLabel = button.dataset.labelHide || 'Hide Details';

      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        var nextState = !expanded;
        button.setAttribute('aria-expanded', String(nextState));
        if (nextState) {
          additional.removeAttribute('hidden');
          if (label) label.textContent = hideLabel;
        } else {
          additional.setAttribute('hidden', '');
          if (label) label.textContent = moreLabel;
        }
      });
    });
  }

  /* ── Card accordions (Pros & Cons / Ratings / Our Take) ───────────
     Each header toggles aria-expanded; CSS sibling selector reveals the
     content panel based on that state. */
  function initCardAccordions(root) {
    var headers = root.querySelectorAll('.best-product__card-accordion-header');
    headers.forEach(function (header) {
      header.addEventListener('click', function () {
        var expanded = header.getAttribute('aria-expanded') === 'true';
        header.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  /* ── Testing-criteria accordion ───────────────────────────────────
     Header is a real <button> with aria-expanded; we toggle the
     associated content's hidden attribute. */
  function initCriteriaAccordion(root) {
    var headers = root.querySelectorAll('.best-product__criteria-button');
    headers.forEach(function (header) {
      header.addEventListener('click', function () {
        var expanded = header.getAttribute('aria-expanded') === 'true';
        var nextState = !expanded;
        header.setAttribute('aria-expanded', String(nextState));
        var contentId = header.getAttribute('aria-controls');
        if (!contentId) return;
        var content = document.getElementById(contentId);
        if (!content) return;
        if (nextState) {
          content.removeAttribute('hidden');
        } else {
          content.setAttribute('hidden', '');
        }
      });
    });
  }

  /* ── Rating bar fill animation ────────────────────────────────────
     Drives the More-Details ratings grid (mattress only — sleep position ×
     weight). Reads data-value off each .graph-bar / .best-product__card-ratings-bar
     and sets the fill width + color class on the inner span. Values are
     0–10; we map 7+ → positive (green), 4–7 → neutral (orange), <4 → negative. */
  function initRatingBars(root) {
    var bars = root.querySelectorAll('.graph-bar, .best-product__card-ratings-bar');
    bars.forEach(function (bar) {
      var value = parseFloat(bar.getAttribute('data-value'));
      if (Number.isNaN(value)) return;
      var fill = bar.querySelector('.graph-bar-fill, .best-product__card-ratings-bar-fill');
      if (!fill) return;

      var colorClass;
      if (value >= 8) {
        colorClass = 'positive';
      } else if (value >= 4) {
        colorClass = 'neutral';
      } else {
        colorClass = 'negative';
      }

      var pct = Math.min(value * 10, 100);
      fill.style.width = pct + '%';
      fill.classList.add(colorClass);
    });
  }

  /* ── Sleep-expert blockquote hydration ────────────────────────────
     Reads server-rendered JSON island; appends author block to any
     <blockquote data-expert-id> referencing a known expert. */
  function initSleepExpertQuotes(root) {
    var island = root.querySelector('[data-best-product-experts]');
    if (!island) return;
    var experts;
    try {
      experts = JSON.parse(island.textContent);
    } catch (err) {
      return;
    }
    var quotes = root.querySelectorAll('blockquote[data-expert-id]');
    quotes.forEach(function (quote) {
      var data = experts[quote.dataset.expertId];
      if (!data || quote.querySelector('.quote-author')) return;
      var author = document.createElement('div');
      author.className = 'quote-author';
      author.innerHTML =
        '<img src="' + (data.avatar || '') + '" alt="" height="40" width="40">' +
        '<span>' + (data.name || '') + '<br><small>' + (data.titles || '') + '</small></span>';
      quote.appendChild(author);
    });
  }

  /* ── Sticky CTA visibility ────────────────────────────────────────
     Becomes visible once the user has scrolled past either:
       - a custom trigger element (data-custom-url starts with #), or
       - the article wrapper (data-trigger-id), or
       - a scroll percentage threshold (data-scroll-percentage > 0).
     Re-hides when the footer enters the viewport. */
  function initStickyCta(root) {
    var section = root.closest('.shopify-section') || document;
    var stickyCta = section.querySelector('[data-sticky-cta]');
    if (!stickyCta) return;

    stickyCta.removeAttribute('hidden');

    var scrollPercentage = parseInt(stickyCta.getAttribute('data-scroll-percentage'), 10) || 0;
    var useScrollPercentage = scrollPercentage > 0;
    var useCustomUrl = stickyCta.getAttribute('data-use-custom-url') === 'true';
    var customUrl = stickyCta.getAttribute('data-custom-url') || '';
    var triggerId = stickyCta.getAttribute('data-trigger-id') || '';

    var triggerElement = null;
    if (!useScrollPercentage) {
      if (useCustomUrl && customUrl && customUrl.charAt(0) === '#') {
        triggerElement = document.getElementById(customUrl.substring(1));
      }
      if (!triggerElement && triggerId) {
        var article = document.getElementById(triggerId);
        if (article) {
          triggerElement = article.querySelector('h1') || article;
        }
      }
      if (!triggerElement) return;
    }

    // Use the page footer landmark, NOT the first `<footer>` element.
    // Each product card renders its own inner `<footer>`, so a plain
    // `footer` selector would match a card's footer (~30% down the page)
    // and skew "is footer visible" calculations.
    var footer = document.querySelector('[role="contentinfo"]')
      || document.querySelector('#shopify-section-footer, .footer.full-width');
    var hasScrolledPastTrigger = false;
    var footerVisible = false;

    function updateVisibility() {
      var show = hasScrolledPastTrigger && !footerVisible;
      stickyCta.classList.toggle('is-visible', show);
    }

    function checkScrollPosition() {
      var scrolledPast = false;
      if (useScrollPercentage) {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        scrolledPast = (scrollTop / docHeight) * 100 >= scrollPercentage;
      } else if (triggerElement) {
        scrolledPast = triggerElement.getBoundingClientRect().bottom < 100;
      }
      if (hasScrolledPastTrigger !== scrolledPast) {
        hasScrolledPastTrigger = scrolledPast;
        updateVisibility();
      }
    }

    var rafId = null;
    window.addEventListener('scroll', function () {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(checkScrollPosition);
    }, { passive: true });

    checkScrollPosition();

    if (footer && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerVisible = entry.isIntersecting;
          updateVisibility();
        });
      }, { threshold: 0, rootMargin: '0px' });
      observer.observe(footer);
    }
  }

  /* ── Table of contents ─────────────────────────────────────────────
     Auto-populates from H2 elements in the article body, sets up
     scroll-spy for the active link, smooth-scrolls on click, and
     adjusts max-height to avoid the footer. */
  function initTableOfContents(root) {
    var section = root.closest('.shopify-section') || document;
    var toc = section.querySelector('[data-toc]');
    if (!toc) return;

    var contentSelector = toc.getAttribute('data-content-selector') || '.best-product__body';
    var introId = toc.getAttribute('data-intro-id') || 'best-product-intro';
    var specialIds = (toc.getAttribute('data-special-ids') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length > 0; });

    var content = document.querySelector(contentSelector);
    var list = toc.querySelector('[data-toc-list]');
    if (!content || !list) return;

    // Drop any static <li> whose target doesn't exist on this page so the
    // TOC never points at a missing anchor.
    Array.prototype.forEach.call(list.querySelectorAll('.best-product__toc-link'), function (a) {
      var hash = a.getAttribute('href') || '';
      var id = hash.charAt(0) === '#' ? hash.slice(1) : hash;
      if (id && id !== introId && !document.getElementById(id)) {
        if (a.parentElement) a.parentElement.remove();
      }
    });

    var headings = content.querySelectorAll('h2');

    Array.prototype.forEach.call(headings, function (heading) {
      if (!heading.id) {
        heading.id = heading.textContent
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      var li = document.createElement('li');
      li.className = 'best-product__toc-item';
      var a = document.createElement('a');
      a.className = 'best-product__toc-link';
      a.href = '#' + heading.id;
      a.textContent = heading.textContent;
      li.appendChild(a);
      list.appendChild(li);
    });

    // If after pruning + heading-injection the list is empty, bail.
    if (list.children.length === 0) return;

    toc.removeAttribute('hidden');

    var stickyHeader = document.querySelector('sticky-header, .header, header');
    var announcementBar = document.querySelector('.announcement-bar, .sleepdoctor-announcement-bar');
    var affiliateBanner = document.querySelector('.best-product__affiliate-banner');

    function getBaseHeaderHeight() {
      var h = 20;
      if (stickyHeader) h += stickyHeader.offsetHeight;
      if (announcementBar) h += announcementBar.offsetHeight;
      return h;
    }

    function getOffset() {
      // Click-scroll offset: header + announcement + a small breathing pad.
      return getBaseHeaderHeight() + 10;
    }

    var links = toc.querySelectorAll('.best-product__toc-link');

    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', function (e) {
        var targetId = link.getAttribute('href');
        var target = document.querySelector(targetId);
        if (!target) return;
        e.preventDefault();
        window.scrollTo({
          top: target.offsetTop - getOffset(),
          behavior: 'smooth'
        });
      });
    });

    function buildSections() {
      var sections = [];
      var introEl = document.getElementById(introId);
      if (introEl) sections.push({ id: introId, top: introEl.offsetTop });

      // Special landmarks (Our Top Picks / Product Details) — these don't
      // have h2s but should appear in the scroll-spy.
      specialIds.forEach(function (id) {
        var el = document.getElementById(id);
        if (el) sections.push({ id: id, top: el.offsetTop });
      });

      Array.prototype.forEach.call(headings, function (h) {
        sections.push({ id: h.id, top: h.offsetTop });
      });
      return sections.sort(function (a, b) { return a.top - b.top; });
    }

    function updateActive() {
      var fromTop = window.scrollY + getOffset() + 50;
      var sections = buildSections();
      var current = null;
      for (var i = sections.length - 1; i >= 0; i--) {
        if (sections[i].top <= fromTop) { current = sections[i]; break; }
      }
      if (!current && sections.length) current = sections[0];
      Array.prototype.forEach.call(links, function (link) {
        link.classList.toggle('is-active', current && link.hash === '#' + current.id);
      });
    }

    // Use the page footer landmark, NOT the first `<footer>` element.
    // Each product card renders its own inner `<footer>`, so a plain
    // `footer` selector would match a card's footer (~30% down the page)
    // and skew "is footer visible" calculations.
    var footer = document.querySelector('[role="contentinfo"]')
      || document.querySelector('#shopify-section-footer, .footer.full-width');

    var CONTENT_WIDTH = 828;
    var TOC_WIDTH = 280;
    var TOC_GAP = 60;

    function updateLayout() {
      // Right-edge anchored to the article column: TOC sits 60px to the
      // right of the centered 828px content. Clamped to a 20px minimum
      // so it never collides with the viewport edge.
      var rightPx = (window.innerWidth - CONTENT_WIDTH) / 2 - TOC_GAP - TOC_WIDTH;
      toc.style.right = Math.max(20, Math.round(rightPx)) + 'px';

      // Top: header + announcement bar. When the affiliate banner is still
      // visible in the viewport, push the TOC below it so they don't overlap.
      var topPx = getBaseHeaderHeight();
      if (affiliateBanner) {
        var bannerRect = affiliateBanner.getBoundingClientRect();
        if (bannerRect.bottom > topPx) {
          topPx = bannerRect.bottom + 10;
        }
      }

      // Footer: rather than shrinking the panel, shift it up so its bottom
      // edge stays `footerGap` above the footer — the panel scrolls up with
      // the page (matches the article TOC). maxHeight reverts to the CSS cap.
      toc.style.maxHeight = '';
      var footerGap = 20;
      if (footer) {
        var footerRect = footer.getBoundingClientRect();
        var maxTopForFooter = footerRect.top - toc.offsetHeight - footerGap;
        if (maxTopForFooter < topPx) topPx = maxTopForFooter;
      }
      toc.style.top = topPx + 'px';
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        updateActive();
        updateLayout();
        ticking = false;
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      window.requestAnimationFrame(function () {
        updateActive();
        updateLayout();
      });
    }, { passive: true });

    updateActive();
    updateLayout();
  }

  function init(root) {
    if (!root) return;
    initStickyTableCells(root);
    initTableHeaders(root);
    initCardDetailsToggle(root);
    initCardAccordions(root);
    initCriteriaAccordion(root);
    initRatingBars(root);
    initSleepExpertQuotes(root);
    initStickyCta(root);
    initTableOfContents(root);
  }

  function bootstrap() {
    document.querySelectorAll('[data-section-type="best-product"], [data-section-type="individual-product"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-section-type="best-product"], [data-section-type="individual-product"]');
    if (root) init(root);
  });
})();
